import { create } from 'zustand';

/**
 * The home Quick Links: which ones the member picked for Home (up to
 * {@link MAX_HOME_QUICK_LINKS}, in their order), and which features they
 * opened last from Explore (its search's "Last used"). Home's order comes only
 * from the picks: opening a link never moves it. Persisted to localStorage;
 * the `paas_` prefix mirrors it to native Preferences via `durablePrefs`.
 */

const RECENT_KEY = 'paas_quick_links_recent';
const PINNED_KEY = 'paas_quick_links_pinned';

export const MAX_HOME_QUICK_LINKS = 8;

interface QuickLinksState {
  /** Feature ids opened from Explore, most recent first. */
  recent: string[];
  /** Quick-link ids the member picked for Home, in order; null until they pick. */
  pinned: string[] | null;
  promote: (id: string) => void;
  setPinned: (ids: string[] | null) => void;
  reset: () => void;
}

function load(key: string): string[] | null {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    if (Array.isArray(parsed)) return parsed.filter((id): id is string => typeof id === 'string');
  } catch {
    /* storage unavailable / malformed — start with tenant order */
  }
  return null;
}

function save(key: string, ids: string[] | null): void {
  try {
    if (ids) localStorage.setItem(key, JSON.stringify(ids));
    else localStorage.removeItem(key);
  } catch (error) {
    console.warn('[quick-links] persist failed:', error);
  }
}

export const useQuickLinksStore = create<QuickLinksState>((set, get) => ({
  recent: load(RECENT_KEY) ?? [],
  pinned: load(PINNED_KEY),
  promote(id) {
    const recent = [id, ...get().recent.filter((existing) => existing !== id)];
    set({ recent });
    save(RECENT_KEY, recent);
  },
  setPinned(ids) {
    set({ pinned: ids });
    save(PINNED_KEY, ids);
  },
  reset() {
    set({ recent: [], pinned: null });
    save(RECENT_KEY, []);
    save(PINNED_KEY, null);
  },
}));

/**
 * Panic, then the admin shortcuts, at the front — admin shortcuts have no other
 * way in — then everything else in its tenant order (the sort is stable).
 */
function panicAndAdminFirst<T extends { isPanic?: boolean; isAdmin?: boolean }>(links: T[]): T[] {
  const rank = (link: T) => (link.isPanic ? 0 : link.isAdmin ? 1 : 2);
  return [...links].sort((a, b) => rank(a) - rank(b));
}

/**
 * The quick links Home shows: the member's picks in their order (skipping any
 * no longer published), or — until they pick — the first
 * {@link MAX_HOME_QUICK_LINKS} with panic and the admin shortcuts first.
 */
export function homeQuickLinks<T extends { id: string; isPanic?: boolean; isAdmin?: boolean }>(
  links: T[],
  pinned: string[] | null,
): T[] {
  if (!pinned) return panicAndAdminFirst(links).slice(0, MAX_HOME_QUICK_LINKS);

  const byId = new Map(links.map((link) => [link.id, link]));
  return pinned.flatMap((id) => byId.get(id) ?? []).slice(0, MAX_HOME_QUICK_LINKS);
}
